/**
 * CI/CD Setup je Space: Auswahl des Space und Karte seiner GitHub-Verbindung.
 *
 * Die Auswahl ist das einzige Bedienelement für den Space auf der Seite. Jeder Eintrag
 * zeigt unter dem Namen den Verbindungsstand als schlichten Text.
 */
import { useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, InputLabel, ListSubheader, MenuItem, Select,
} from "@mui/material";

const FIELD_SX = {
  minWidth: 0,
  "& .MuiInputLabel-root": { color: "var(--iio-text-2)", fontFamily: "inherit" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--iio-info)" },
  "& .MuiOutlinedInput-root": {
    background: "var(--iio-bg-1)", color: "var(--iio-text-1)", fontFamily: "inherit", fontSize: 14, borderRadius: "6px",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-border)" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-text-4)" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--iio-info)", borderWidth: 1 },
  "& .MuiSelect-icon": { color: "var(--iio-text-3)" },
  "& .Mui-disabled": { WebkitTextFillColor: "var(--iio-text-3)" },
};
const MENU_PAPER_SX = {
  background: "var(--iio-bg-1)", color: "var(--iio-text-1)", border: "1px solid var(--iio-border)", borderRadius: "8px",
  "& .MuiListSubheader-root": {
    background: "var(--iio-bg-1)", color: "var(--iio-text-3)", fontSize: 12, fontWeight: 600, lineHeight: "32px", fontFamily: "inherit",
  },
  "& .MuiMenuItem-root": { fontFamily: "inherit", alignItems: "flex-start", flexDirection: "column", gap: "2px", py: 1 },
  "& .MuiMenuItem-root:hover": { background: "var(--iio-bg-2)" },
  "& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover": {
    background: "color-mix(in srgb, var(--iio-info) 8%, var(--iio-bg-1))",
  },
};

/** Host eines GitHub-Servers für die Anzeige. Leer bedeutet github.com. */
export function verbindungsHost(baseUrl) {
  const raw = String(baseUrl || "").trim();
  if (!raw) return "github.com";
  try {
    const host = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).host;
    return host.replace(/^api\./i, "") || "github.com";
  } catch {
    return raw;
  }
}

/** Verbindungsstand eines Space als Satz für die Auswahl. Unbekannter Stand liefert "". */
export function verbindungsStand(t, connection) {
  if (connection === undefined) return "";
  if (!connection || !connection.source) return t("spaceWahl.stand.keine");
  if (connection.source === "personal") return t("spaceWahl.stand.personal");
  const host = verbindungsHost(connection.baseUrl);
  if (connection.kind === "app") return t("spaceWahl.stand.app", { app: connection.appSlug || connection.account || "", host });
  return t("spaceWahl.stand.token", { host });
}

export function SpaceAuswahl({ t, freie, belegte, standJeSpace, value, onChange, disabled }) {
  const eintrag = sp => {
    const stand = verbindungsStand(t, standJeSpace(sp.id));
    return (
      <MenuItem key={sp.id} value={sp.id}>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--iio-text-1)" }}>{sp.name}</span>
        {stand && <span style={{ fontSize: 12, color: "var(--iio-text-3)", whiteSpace: "normal" }}>{stand}</span>}
      </MenuItem>
    );
  };
  const namen = Object.fromEntries([...freie, ...belegte].map(sp => [sp.id, sp.name]));
  return (
    <FormControl size="small" fullWidth sx={FIELD_SX} disabled={disabled}>
      <InputLabel id="cs-space-label" shrink>{t("spaceWahl.titel")}</InputLabel>
      <Select
        labelId="cs-space-label"
        label={t("spaceWahl.titel")}
        notched
        displayEmpty
        value={namen[value] ? value : ""}
        onChange={event => onChange(event.target.value)}
        renderValue={selected => (selected ? namen[selected] : <span style={{ color: "var(--iio-text-3)" }}>{t("spaceWahl.leer")}</span>)}
        SelectDisplayProps={{ "aria-labelledby": "cs-space-label" }}
        MenuProps={{ PaperProps: { className: "iio", sx: MENU_PAPER_SX } }}
      >
        <MenuItem value="">
          <span style={{ fontSize: 14, color: "var(--iio-text-2)" }}>{t("spaceWahl.leer")}</span>
        </MenuItem>
        {freie.map(eintrag)}
        {belegte.length > 0 && <ListSubheader>{t("vorschau.belegteGruppe")}</ListSubheader>}
        {belegte.map(eintrag)}
      </Select>
    </FormControl>
  );
}

const DIALOG_PAPER = {
  className: "iio",
  sx: {
    background: "var(--iio-bg-1)", color: "var(--iio-text-1)", border: "1px solid var(--iio-border)",
    borderRadius: "12px", fontFamily: "inherit", maxWidth: 480,
  },
};
const DIALOG_TEXT_SX = { color: "var(--iio-text-2)", fontFamily: "inherit", fontSize: 14 };
const BTN_SX = { textTransform: "none", fontFamily: "inherit", fontWeight: 500, fontSize: 13, borderRadius: "6px", boxShadow: "none" };

/** Bestätigung per MUI-Dialog (kein window.confirm). Ein Fehler bleibt im Dialog stehen. */
export function BestaetigungsDialog({ t, id, open, titel, text, bestaetigen, gefahr, busy, error, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} aria-labelledby={id} PaperProps={DIALOG_PAPER}>
      <DialogTitle id={id} sx={{ fontFamily: "inherit", fontSize: 16, fontWeight: 600, color: "var(--iio-text-1)" }}>{titel}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={DIALOG_TEXT_SX}>{text}</DialogContentText>
        {error && <div className="cs-msg crit" role="alert" style={{ marginTop: 12 }}><span className="tx">{error}</span></div>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} sx={{ ...BTN_SX, color: "var(--iio-text-2)" }}>{t("gemeinsam:aktion.abbrechen")}</Button>
        <Button onClick={onConfirm} disabled={busy} variant="contained" disableElevation
          sx={{ ...BTN_SX, background: gefahr ? "var(--iio-critical)" : "var(--iio-info)", color: "var(--iio-bg-1)",
            "&:hover": { background: gefahr ? "var(--iio-critical)" : "var(--iio-info)" } }}>
          {bestaetigen}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Karte der eigenen Verbindung des Space: Testen, Ersetzen (Formular oder persönliche Verbindung)
 * und Entfernen. Solange laufende Vorgänge die Verbindung nutzen (inUse), sind Ersetzen und
 * Entfernen gesperrt.
 */
export function SpaceVerbindungKarte({ t, gebiet, spaceName, connection, busy, inUse, test, hinweis, onTest, onRemove, onErsetzen, onUebernehmen }) {
  const [frage, setFrage] = useState(false);
  const [fehler, setFehler] = useState("");
  const host = verbindungsHost(connection.baseUrl);
  const istApp = connection.kind === "app";
  const gesperrt = busy || inUse;
  const beschreibung = inUse ? "cs-space-in-use" : undefined;
  return (
    <>
      <div className="cs-conn on" role="group" aria-label={t("spaceVerbindung.titel", { space: spaceName })}>
        <div className="cs-conn-ic">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
        </div>
        <div className="cs-conn-tx">
          <div className="t">{istApp ? t("spaceVerbindung.appTitel", { app: connection.appSlug || connection.account || "" }) : t("spaceVerbindung.tokenTitel")}</div>
          <div className="d" style={{ display: "flex", flexWrap: "wrap", columnGap: 12 }}>
            <span>{t("spaceVerbindung.server", { host })}</span>
            {(connection.installationOwner || connection.account) && <span>{t("spaceVerbindung.konto", { konto: connection.installationOwner || connection.account })}</span>}
            {connection.createdAt && <span>{t("spaceVerbindung.eingerichtetAm", { zeit: new Date(connection.createdAt).toLocaleString(gebiet) })}</span>}
          </div>
        </div>
        <div className="cs-conn-actions">
          <button type="button" className="cs-btn ghost" onClick={onTest} disabled={busy || test?.loading}>
            {t(test?.loading ? "spaceVerbindung.testLaeuft" : "spaceVerbindung.testen")}
          </button>
          {onErsetzen && <button type="button" className="cs-btn ghost" onClick={onErsetzen} disabled={gesperrt} aria-describedby={beschreibung}>
            {t("spaceVerbindung.ersetzen")}
          </button>}
          {onUebernehmen && <button type="button" className="cs-btn ghost" onClick={onUebernehmen} disabled={gesperrt} aria-describedby={beschreibung}>
            {t("spaceVerbindung.uebernehmen")}
          </button>}
          <button type="button" className="cs-btn danger" onClick={() => { setFehler(""); setFrage(true); }} disabled={gesperrt} aria-describedby={beschreibung}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
            {t("spaceVerbindung.entfernen")}
          </button>
        </div>
      </div>
      {inUse && <div className="cs-hint" id="cs-space-in-use" style={{ marginTop: "var(--iio-s-2)" }}>{t("spaceVerbindung.inUse")}</div>}
      {hinweis && (
        <div className="cs-msg warn" role="status" style={{ marginTop: "var(--iio-s-3)" }}>
          <span className="tx">{hinweis}</span>
        </div>
      )}
      {test?.result && (
        <div className={"cs-msg " + (test.result.ok ? "ok" : "crit")} role="status" style={{ marginTop: "var(--iio-s-3)" }}>
          <span className="tx">
            {test.result.ok
              ? t("spaceVerbindung.testOk", { konto: test.result.account || "", host: verbindungsHost(test.result.baseUrl || connection.baseUrl) })
              : t("spaceVerbindung.testFehler", { grund: test.result.error || "" })}
            {test.result.ok && test.result.repoVisible === true && <span style={{ display: "block" }}>{t("spaceVerbindung.repoSichtbar")}</span>}
            {test.result.ok && test.result.repoVisible === false && <span style={{ display: "block" }}>{t("spaceVerbindung.repoUnsichtbar")}</span>}
          </span>
        </div>
      )}
      {test?.error && (
        <div className="cs-msg crit" role="alert" style={{ marginTop: "var(--iio-s-3)" }}>
          <span className="tx">{t("spaceVerbindung.testFehler", { grund: test.error })}</span>
        </div>
      )}
      <BestaetigungsDialog t={t} id="cs-space-remove-title" open={frage} gefahr busy={busy} error={fehler}
        titel={t("spaceVerbindung.entfernenTitel")} text={t("spaceVerbindung.entfernenText", { space: spaceName })}
        bestaetigen={t("spaceVerbindung.entfernen")}
        onCancel={() => { setFrage(false); setFehler(""); }}
        onConfirm={async () => {
          setFehler("");
          // Ein Fehler (z. B. SPACE_CONNECTION_IN_USE) bleibt im Dialog stehen, der Dialog bleibt offen.
          const ergebnis = await onRemove();
          if (ergebnis?.error) setFehler(ergebnis.error);
          else if (ergebnis?.ok) setFrage(false);
        }}/>
    </>
  );
}
